import { useMediaQuery } from 'react-responsive'

export type ScreenType = 'desktop' | 'tablet' | 'mobile'
export type ScreenOrientation = 'portrait' | 'landscape'

/**
 * Screen widths
 * desktop width > 1023
 * tablet width = 1023 - 768
 * mobile width = < 768
 */

type ScreenSizeChangeCallback = () => void

type ScreenSizeInfoProps = {
  onChange?: ScreenSizeChangeCallback
}

/**
 * This function returns metadata on device screen.
 *
 * {
 * type: 'desktop' | 'tablet' | 'mobile',
 * orientation: 'portrait' | 'landscape',
 * isDesktop: <boolean>,
 * isTablet: <boolean>,
 * isMobile: <boolean>,
 * isRetina: <boolean>,
 * }
 *
 * This function also acceps a callback function to be called
 * when the screen size or orientation changes.
 */

export const useScreenSize = ({ onChange }: ScreenSizeInfoProps = {}): {
  type: ScreenType
  orientation: ScreenOrientation
  isRetina: boolean
  isDesktop: boolean
  isTablet: boolean
  isMobile: boolean
  isPortrait: boolean
  isLandscape: boolean
} => {
  const screenSizeChangeCallback = (matches: boolean) => {
    if (matches && onChange) {
      onChange()
    }
  }

  const isDesktop = useMediaQuery(
    { minWidth: 1024 },
    undefined,
    screenSizeChangeCallback,
  )

  const isTablet = useMediaQuery(
    { maxWidth: 1023, minWidth: 768 },
    undefined,
    screenSizeChangeCallback,
  )

  const isMobile = useMediaQuery(
    { maxWidth: 767 },
    undefined,
    screenSizeChangeCallback,
  )

  const isPortrait = useMediaQuery(
    { orientation: 'portrait' },
    undefined,
    screenSizeChangeCallback,
  )

  const isRetina = useMediaQuery(
    { query: '(min-resolution: 2dppx)' },
    undefined,
    screenSizeChangeCallback,
  )

  const getScreenType = (): ScreenType => {
    if (isTablet) {
      return 'tablet'
    }

    if (isMobile) {
      return 'mobile'
    }

    return 'desktop'
  }

  const getScreenOrientation = (): ScreenOrientation => {
    if (isPortrait) {
      return 'portrait'
    }

    return 'landscape'
  }

  return {
    type: getScreenType(),
    orientation: getScreenOrientation(),
    isRetina,
    isDesktop,
    isTablet,
    isMobile,
    isPortrait,
    isLandscape: !isPortrait,
  }
}
